import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import { authService } from '../services/authService';

export const AuthContext = createContext();

const INACTIVITY_TIMEOUT = 20 * 60 * 1000; // 20 minutes

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState(null);
  const inactivityTimer = useRef(null);

  // Check if user is already logged in
  useEffect(() => {
    const checkUserStatus = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');

      if (token && savedUser) {
        try {
          const response = await authService.getCurrentUser();
          setUser(response.data.user);
          setIsAuthenticated(true);
        } catch (err) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setIsAuthenticated(false);
        }
      }
      setLoading(false);
    };

    checkUserStatus();
  }, []);

  const login = async (username, password) => {
    setError(null);
    try {
      const response = await authService.login(username, password);

      if (response.data.mfaRequired) {
        // Return temp token for MFA verification
        return {
          success: true,
          mfaRequired: true,
          tempToken: response.data.tempToken,
        };
      }

      if (response.data.mustChangePassword) {
        // Return change-password token — do NOT save session
        return {
          success: true,
          mustChangePassword: true,
          changePwToken: response.data.changePwToken,
          reason: response.data.reason,
        };
      }

      // Save token and user
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      setIsAuthenticated(true);

      return { success: true, mfaRequired: false, user: response.data.user };
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Login failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const verifyMFA = async (code, tempToken) => {
    setError(null);
    try {
      const response = await authService.verifyMFA(code, tempToken);

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      setIsAuthenticated(true);

      return { success: true, user: response.data.user };
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'MFA verification failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const register = async (firstName, lastName, username, employeeId, email, password, department) => {
    setError(null);
    try {
      const response = await authService.register({
        firstName,
        lastName,
        username,
        employeeId,
        email,
        password,
        department,
      });

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      setIsAuthenticated(true);

      return { success: true };
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Registration failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const setupMFA = async () => {
    setError(null);
    try {
      const response = await authService.setupMFA();
      return {
        success: true,
        qrCode: response.data.qrCode,
        secret: response.data.secret,
        backupCodes: response.data.backupCodes,
      };
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'MFA setup failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const confirmMFA = async (secret, code) => {
    setError(null);
    try {
      const response = await authService.confirmMFA(secret, code);
      // Update user state to reflect MFA enabled
      setUser({ ...user, mfaEnabled: true });
      return {
        success: true,
        backupCodes: response.data.backupCodes,
      };
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'MFA confirmation failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const logout = useCallback(() => {
    clearTimeout(inactivityTimer.current);
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
  }, []);

  // Auto-logout after 20 minutes of inactivity
  useEffect(() => {
    if (!isAuthenticated) return;

    const resetTimer = () => {
      clearTimeout(inactivityTimer.current);
      inactivityTimer.current = setTimeout(() => {
        logout();
        window.location.href = '/login';
      }, INACTIVITY_TIMEOUT);
    };

    const events = ['mousemove', 'mousedown', 'keypress', 'touchstart', 'scroll', 'click'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      clearTimeout(inactivityTimer.current);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [isAuthenticated, logout]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated,
        error,
        login,
        register,
        logout,
        verifyMFA,
        setupMFA,
        confirmMFA,
        setError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
